/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Modal, Spin } from 'antd'
import { CloseOutlined, TeamOutlined } from '@ant-design/icons'
import React, { useEffect } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import { useIsOverflowing } from 'src/hooks/useIsOverflowing'
import {
  font,
  weightMedium,
  weightSemiBold,
  paragraphSmall,
  paragraphMedium,
  paragraphLarge,
} from 'src/components/css'
import { useFamilyMembers } from '../hooks/useFamilyMembers'
import FamilyGuideLink from './components/FamilyGuideLink'
import FamilyMemberRow from './components/FamilyMemberRow'
import { FAMILY_SUPPORT_URL } from '@sportninja/common/features/profile/family/constants'

interface Props {
  isOpen: boolean
  onClose: () => void
  playerId: string | null
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
}

/**
 * Family Members modal — opened from the player header for a non-primary
 * viewer. A read-only grouped family list; managing family lives in the Edit
 * Player modal.
 */
const FamilyManagementModal: React.FC<Props> = ({
  isOpen,
  onClose,
  playerId,
}) => {
  'use memo'
  const { groups, isLoading, error, refetch } = useFamilyMembers(playerId ?? '')

  // Always pull fresh data when the modal opens — the list can go stale while
  // closed (a member accepted, was edited, etc.).
  useEffect(() => {
    if (isOpen && playerId) {
      refetch()
    }
  }, [isOpen, playerId, refetch])

  // Add right padding only when the list scrolls, so rows clear the scrollbar
  // without leaving dead space on the right when everything fits.
  const [listRef, isOverflowing] = useIsOverflowing<HTMLDivElement>([
    isOpen,
    groups,
  ])

  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 16px;
      `}
    />
  )

  const header = (
    <div css={headerRowStyle}>
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        {capitalizeWords('Family Members')}
      </Text>
      <FamilyGuideLink size={22} />
    </div>
  )

  if (!isOpen) {
    return null
  }

  return (
    <>
      <Modal
        css={modalCss}
        width={600}
        closeIcon={closeIcon}
        title={header}
        footer={null}
        open={isOpen}
        onCancel={onClose}
        centered
        styles={modalStyles}
      >
        <div css={bodyStyle}>
          {/* Scrollable list region — caps height and scrolls when there are
              many members. */}
          <div
            ref={listRef}
            css={[listScrollStyle, isOverflowing && listScrollOverflowStyle]}
          >
            {isLoading ? (
              <div css={centeredStyle}>
                <Spin />
              </div>
            ) : error ? (
              <div css={centeredStyle}>
                <span css={errorTextStyle}>
                  Couldn&apos;t load family members.
                </span>
              </div>
            ) : groups.length === 0 ? (
              <div css={emptyStateStyle}>
                <div css={emptyIconWrapStyle}>
                  <TeamOutlined css={emptyIconStyle} />
                </div>
                <span css={emptyTextStyle}>
                  {capitalizeWords('No family members yet')}
                </span>
                <span css={emptyHintStyle}>
                  For more information about this feature,{' '}
                  <a
                    css={emptyLinkStyle}
                    href={FAMILY_SUPPORT_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Click Here.
                  </a>
                </span>
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.group} css={groupSectionStyle}>
                  <span css={sectionLabelStyle}>{g.title}</span>
                  <div css={memberListStyle}>
                    {g.members.map((member) => (
                      <FamilyMemberRow key={member.id} member={member} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>
    </>
  )
}

export default FamilyManagementModal

// ─── Styles ────────────────────────────────────────────────────────────────

const modalCss = css`
  display: flex;
  flex: 1;
  max-height: 90vh;
  margin: 24px auto;
  padding: 0;

  .ant-modal-content {
    max-height: 90vh;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`

const headerRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  flex: 1;
  min-height: 0;
`

// Only the member list scrolls.
const listScrollStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  min-height: 0;
  /* Cap so ~5-6 rows show before scrolling; modal still respects 90vh too. */
  max-height: 420px;
`

// Applied only when the list actually overflows, so rows clear the scrollbar
// without adding dead space on the right when everything fits.
const listScrollOverflowStyle = css`
  padding-right: 12px;
`

const groupSectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const memberListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const sectionLabelStyle = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 14px;
  line-height: 120%;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_100};
  display: block;
`

const centeredStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
`

const emptyStateStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px;
  text-align: center;
`

const emptyIconWrapStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 10px;
  border-radius: 50%;
  background: ${colors.NEUTRAL_700};
`

const emptyIconStyle = css`
  font-size: 26px;
  color: ${colors.NEUTRAL_50};
`

const errorTextStyle = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_100};
`

const emptyTextStyle = css`
  ${paragraphLarge}
  ${weightMedium}
  ${font.body}
  color: ${colors.NEUTRAL_0};
`

const emptyHintStyle = css`
  ${paragraphMedium}
  ${font.body}
  color: ${colors.NEUTRAL_50};
`

const emptyLinkStyle = css`
  ${weightMedium}
  color: ${colors.PRIMARY};
  text-decoration: none;

  &:hover {
    color: ${colors.PRIMARY} !important;
    opacity: 0.7;
  }
`
